import React, { useEffect, useState } from 'react';
import { useGameStore } from '../store/gameStore';

interface PerformanceStats {
  fps: number;
  memoryUsage: number;
  renderTime: number;
  cacheHits: number;
  cacheMisses: number;
}

const PerformanceMonitor: React.FC = () => {
  const { playerLevel, settings } = useGameStore();
  const [stats, setStats] = useState<PerformanceStats>({
    fps: 60,
    memoryUsage: 0,
    renderTime: 0,
    cacheHits: 0,
    cacheMisses: 0,
  });
  const [isVisible, setIsVisible] = useState(false);

  // Zeige Performance-Monitor nur wenn manuell aktiviert
  useEffect(() => {
    setIsVisible(settings.performanceMonitorEnabled);
  }, [settings.performanceMonitorEnabled]);

  useEffect(() => {
    if (!isVisible) return;

    let frameCount = 0;
    let lastTime = performance.now();
    let animationId: number | null = null;

    const measurePerformance = () => {
      frameCount++;
      const currentTime = performance.now();
      
      if (currentTime - lastTime >= 1000) {
        const fps = Math.round((frameCount * 1000) / (currentTime - lastTime));
        
        // Memory Usage (nur wenn verfügbar) - mit Throttling
        let memoryUsage = 0;
        if ('memory' in performance) {
          memoryUsage = (performance as any).memory.usedJSHeapSize / 1024 / 1024; // MB
        }
        
        setStats(prev => ({
          ...prev,
          fps,
          memoryUsage: Math.round(memoryUsage * 100) / 100,
        }));
        
        frameCount = 0;
        lastTime = currentTime;
      }
      
      animationId = requestAnimationFrame(measurePerformance);
    };

    animationId = requestAnimationFrame(measurePerformance);

    return () => {
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  }, [isVisible]);

  if (!isVisible) return null;

  const getPerformanceColor = (fps: number) => {
    if (fps >= 50) return 'text-green-500';
    if (fps >= 30) return 'text-yellow-500';
    return 'text-red-500';
  };

  const getMemoryColor = (memory: number) => {
    if (memory < 50) return 'text-green-500';
    if (memory < 100) return 'text-yellow-500';
    return 'text-red-500';
  };

  return (
    <div className="fixed bottom-4 right-4 bg-black bg-opacity-75 text-white p-3 rounded-lg text-xs z-50">
      <div className="font-bold mb-2">⚡ Performance Monitor</div>
      <div className="space-y-1">
        <div className={`${getPerformanceColor(stats.fps)}`}>
          FPS: {stats.fps}
        </div>
        <div className={`${getMemoryColor(stats.memoryUsage)}`}>
          Memory: {stats.memoryUsage} MB
        </div>
        <div className="text-gray-300">
          Level: {playerLevel.toLocaleString()}
        </div>
      </div>
      
      {/* Performance-Tipps */}
      {stats.fps < 30 && (
        <div className="mt-2 p-2 bg-red-500 bg-opacity-20 rounded text-xs">
          ⚠️ Niedrige FPS - Schließe andere Tabs oder reduziere Browser-Tabs
        </div>
      )}
      
      {stats.memoryUsage > 100 && (
        <div className="mt-2 p-2 bg-yellow-500 bg-opacity-20 rounded text-xs">
          ⚠️ Hoher Speicherverbrauch - Lade die Seite neu
        </div>
      )}
    </div>
  );
};

export default PerformanceMonitor; 